import type {
  ConnectionView,
  WorkspaceSecretView,
} from '@/lib/api/generated/model';

export type SecretKindFilter = 'all' | 'secret' | 'variable' | 'managed';

/**
 * The chip and the text combine (spec 229 FR-008). "Secrets" and "Variables"
 * go by kind over every entry, managed ones included; "Managed" is the
 * entries a connection wrote. Text matches the key or the description: once
 * prose is on screen people type words from it.
 */
export function filterSecrets(
  entries: WorkspaceSecretView[],
  query: string,
  kind: SecretKindFilter,
): WorkspaceSecretView[] {
  const needle = query.trim().toLowerCase();
  return entries.filter(
    (entry) =>
      (kind === 'all' ||
        (kind === 'managed'
          ? entry.managed_by !== null
          : entry.kind === kind)) &&
      (needle === '' ||
        entry.key.toLowerCase().includes(needle) ||
        (entry.description ?? '').toLowerCase().includes(needle)),
  );
}

/** Chip counts over the whole list; managed overlaps the two kinds (research R7). */
export function secretCounts(
  entries: WorkspaceSecretView[],
): Record<SecretKindFilter, number> {
  return {
    all: entries.length,
    secret: entries.filter((entry) => entry.kind === 'secret').length,
    variable: entries.filter((entry) => entry.kind === 'variable').length,
    managed: entries.filter((entry) => entry.managed_by !== null).length,
  };
}

export interface SecretGroup {
  /** `custom`, or the managing connection's id. */
  key: string;
  /** "Custom", or the managing connection's name. */
  title: string;
  connectionId?: string;
  connectionType?: string;
  /** The connection the rows were read with, when it is still here. */
  connection?: ConnectionView;
  /** A managed group whose connection is no longer in the list. */
  disconnected: boolean;
  entries: WorkspaceSecretView[];
}

/**
 * The visible entries as one list in groups (spec 229 FR-011): the custom
 * ones first, then one group per managing connection in the order its first
 * entry appears. A group with nothing visible is left out, so a search that
 * matches a group's name but none of its rows hides the group like any row.
 */
export function groupSecrets(
  visible: WorkspaceSecretView[],
  connections: ConnectionView[] | undefined,
): SecretGroup[] {
  const custom = visible.filter((entry) => entry.managed_by === null);
  const managed = new Map<string, SecretGroup>();
  for (const entry of visible) {
    if (!entry.managed_by) continue;
    const { connection_id, type, name } = entry.managed_by;
    const group = managed.get(connection_id) ?? {
      key: connection_id,
      title: name,
      connectionId: connection_id,
      connectionType: type,
      connection: connections?.find((c) => c.id === connection_id),
      disconnected:
        connections !== undefined &&
        !connections.some((c) => c.id === connection_id),
      entries: [],
    };
    group.entries.push(entry);
    managed.set(connection_id, group);
  }
  return [
    ...(custom.length > 0
      ? [
          {
            key: 'custom',
            title: 'Custom',
            disconnected: false,
            entries: custom,
          },
        ]
      : []),
    ...managed.values(),
  ];
}
